import approve from 'approvejs';
import { useMemo } from 'react';
import { getTranslationKey } from '../../../modules/utils';
import { ICountry } from '../../../modules/types';
import { getPostalCodeError } from '../../../common/postal-code-validation';

export interface Ifields {
  field: string;
  type: string;
  placeholder: string;
  title: string;
  rules: any;
  validators?: any[];
}

export interface seperateStreetFieldResponse {
  street: string | null;
  streetNumber: string | null;
}

export function getValidate(rules: any) {
  return (value: any) => {
    const result = approve.value(value, rules);
    if (result.errors.length > 0) {
      return result.errors;
    }

    return null;
  };
}

/**
 * Builds the redux-form field validator for the zipcode field. The countries
 * list has to be passed in because the postal code format is looked up by the
 * country's ISO code, which only the API's countries list knows.
 */
export function createValidateZip(countriesList: ICountry[]) {
  return (zipCode: any, formValues: any) => {
    const error = getPostalCodeError(zipCode, formValues?.country, countriesList, {
      fallbackToDigitsOnly: true
    });

    return error ? [error] : null;
  };
}

export const getEmailField = () => ({
  field: 'email',
  type: 'email',
  rules: {
    required: { message: getTranslationKey('requiredInput') },
    email: {
      message: getTranslationKey('requiredEMailInput')
    }
  }
});

export const getCompanyField = () => ({
  field: 'company',
  type: 'text',
  rules: {
    required: { message: getTranslationKey('requiredInput') }
  }
});

export const getCol6Fields = () =>
  [
    {
      field: 'firstName',
      type: 'text'
    },
    {
      field: 'lastName',
      type: 'text'
    },
    {
      field: 'street',
      type: 'text'
    },
    {
      field: 'streetNumber',
      type: 'text'
    },
    {
      field: 'zipcode',
      type: 'text'
    },
    {
      field: 'city',
      type: 'text'
    }
  ].map((field) => ({
    ...field,
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  }));

export function useFormFields(countriesList: ICountry[]) {
  // Keep one stable validator instance per countries list — redux-form
  // re-registers the field whenever the `validate` identity changes.
  const validateZip = useMemo(() => createValidateZip(countriesList), [countriesList]);

  const emailFieldTranslated: Ifields = {
    ...getEmailField(),
    placeholder: getTranslationKey('email'),
    title: `${getTranslationKey('email')}*`,
    rules: {
      required: { message: getTranslationKey('requiredInput') },
      email: {
        message: getTranslationKey('requiredEMailInput')
      }
    }
  };
  emailFieldTranslated.validators = [getValidate(emailFieldTranslated.rules)];

  const companyFieldTranslated: Ifields = {
    ...getCompanyField(),
    placeholder: getTranslationKey('company'),
    title: `${getTranslationKey('company')}*`,
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  };
  companyFieldTranslated.validators = [getValidate(companyFieldTranslated.rules)];

  const col6Fields = getCol6Fields();
  const col6FieldsTranslated: Ifields[] = [
    {
      ...col6Fields[0],
      placeholder: getTranslationKey('firstname'),
      title: `${getTranslationKey('firstname')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    },
    {
      ...col6Fields[1],
      placeholder: getTranslationKey('lastname'),
      title: `${getTranslationKey('lastname')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    },
    {
      ...col6Fields[2],
      placeholder: getTranslationKey('street'),
      title: `${getTranslationKey('street')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    },
    {
      ...col6Fields[3],
      placeholder: getTranslationKey('streetNumber'),
      title: `${getTranslationKey('streetNumber')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    },
    {
      ...col6Fields[4],
      placeholder: getTranslationKey('zipcode'),
      title: `${getTranslationKey('zipcode')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    },
    {
      ...col6Fields[5],
      placeholder: getTranslationKey('city'),
      title: `${getTranslationKey('city')}*`,
      rules: {
        required: { message: getTranslationKey('requiredInput') }
      }
    }
  ];
  col6FieldsTranslated.forEach((field) => {
    field.validators =
      field.field === 'zipcode'
        ? [getValidate(field.rules), validateZip]
        : [getValidate(field.rules)];
  });

  return {
    emailField: emailFieldTranslated,
    companyField: companyFieldTranslated,
    col6Fields: col6FieldsTranslated,
    firstNameField: col6FieldsTranslated.find((field) => field.field === 'firstName'),
    lastNameField: col6FieldsTranslated.find((field) => field.field === 'lastName'),
    streetField: col6FieldsTranslated.find((field) => field.field === 'street'),
    streetNumberField: col6FieldsTranslated.find((field) => field.field === 'streetNumber'),
    zipcodeField: col6FieldsTranslated.find((field) => field.field === 'zipcode'),
    cityField: col6FieldsTranslated.find((field) => field.field === 'city')
  };
}

export const getCountriesOptions = (list: ICountry[]) =>
  list.map((country) => ({
    value: country.id,
    displayValue: country.name
  }));

export const seperateStreetField = (street: string): seperateStreetFieldResponse => {
  const streetArr = street?.match(/(\D+)\s+(\d+)/);

  return {
    street: streetArr && streetArr[1] ? streetArr[1] : street,
    streetNumber: streetArr && streetArr[2] ? streetArr[2] : null
  };
};
